import { getToolName } from "ai";
import { memo } from "react";
import cx from "classnames";

import Box from "ds/components/Box";
import Typography from "ds/components/Typography";
import MarkdownAssistantPanel from "components/markdown/MarkdownAssistantPanel";
import { IconComponent } from "types/Icon";
import { withTestId } from "utils/withTestId";

import { AiChatMessage } from "../../types";
import { AI_CHAT_ASSISTANT_MESSAGE_TEST_ID, AI_CHAT_USER_MESSAGE_TEST_ID } from "../../constants";
import AvatarAnimated from "../../Avatar/Animated";
import AvatarStatic from "../../Avatar/Static";
import AiChatMessageActions from "../../MessageActions";
import AiChatThinking from "../../Thinking";
import AiChatToolCall from "../../Tool";
import { ToolPart } from "../../Tool/helpers";
import { getMessageSegments, getMessageText, isGroupLive } from "../helpers";
import styles from "./styles.module.css";

type AiChatMessagesItemProps = {
  message: AiChatMessage;
  isLast: boolean;
  isStreaming: boolean;
  toolIcons?: Record<string, IconComponent>;
  onApproveTool?: (part: ToolPart) => void;
  onRejectTool?: (part: ToolPart) => void;
  onReusePrompt?: (text: string) => void;
};

const AiChatMessagesItem = ({
  message,
  isLast,
  isStreaming,
  toolIcons,
  onApproveTool,
  onRejectTool,
  onReusePrompt,
}: AiChatMessagesItemProps) => {
  const messageText = getMessageText(message);

  if (message.role === "user") {
    return (
      <Box
        gap="medium"
        justify="end"
        align="end"
        className={styles.message}
        {...withTestId(AI_CHAT_USER_MESSAGE_TEST_ID)}
      >
        <Box className={styles.userMessageContent}>
          {message.parts.map((part, index) => {
            if (part.type === "text") {
              return (
                <Typography key={index} tag="p" variant="p-body3-multiline">
                  {part.text}
                </Typography>
              );
            }
            return null;
          })}
        </Box>
        <AiChatMessageActions
          text={messageText}
          justify="end"
          className={styles.messageActions}
          onReuse={onReusePrompt ? () => onReusePrompt(messageText) : undefined}
        />
      </Box>
    );
  }

  const renderTool = (part: ToolPart) => (
    <AiChatToolCall
      className={styles.toolCall}
      icon={toolIcons?.[getToolName(part)]}
      part={part}
      onApprove={onApproveTool ? () => onApproveTool(part) : undefined}
      onReject={onRejectTool ? () => onRejectTool(part) : undefined}
    />
  );

  const segments = getMessageSegments(message.parts);

  return (
    <Box
      direction="column"
      align="start"
      gap="medium"
      className={cx(styles.assistantMessage, styles.message)}
      {...withTestId(AI_CHAT_ASSISTANT_MESSAGE_TEST_ID)}
    >
      {isStreaming ? <AvatarAnimated /> : <AvatarStatic />}
      <Box direction="column" gap="x-large" zeroMinWidth fullWidth>
        {segments.map((segment, index) => {
          if (segment.type === "group") {
            const isLive =
              isLast && index === segments.length - 1 && isGroupLive(segment.parts, isStreaming);

            return (
              <AiChatThinking
                key={segment.key}
                parts={segment.parts}
                isLive={isLive}
                renderTool={renderTool}
              />
            );
          }

          const { key, part } = segment;

          switch (part.type) {
            case "text":
              return <MarkdownAssistantPanel key={key}>{part.text}</MarkdownAssistantPanel>;
            case "source-url":
              return (
                <Typography key={key} tag="p" variant="p-body3" color="secondary">
                  {part.title || part.url}
                </Typography>
              );
            case "source-document":
              return (
                <Typography key={key} tag="p" variant="p-body3" color="secondary">
                  {part.title}
                </Typography>
              );
            case "file":
              return part.mediaType.startsWith("image/") ? (
                <img
                  key={key}
                  src={part.url}
                  alt={part.filename || "file"}
                  className={styles.fileImage}
                />
              ) : (
                <Typography key={key} tag="p" variant="p-body3" color="secondary">
                  {part.filename || part.mediaType}
                </Typography>
              );
            case "step-start":
            case "custom":
            case "reasoning":
            case "dynamic-tool":
            case "reasoning-file":
            default:
              return null;
          }
        })}
      </Box>
      {messageText && !isStreaming && (
        <AiChatMessageActions text={messageText} className={styles.messageActions} />
      )}
    </Box>
  );
};

export default memo(AiChatMessagesItem);
